import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import * as AssistantActions from '../../state/assistant.actions';
import * as CrmActions from '../../../store/crm/crm.actions';
import {
  selectAssistantError,
  selectAssistantLoading,
  selectAssistantMessages,
  selectCurrentConversationId,
  selectConversations,
  selectConversationsLoading,
  selectPendingWrite,
  selectAdminSessionId,
} from '../../state/assistant.selectors';
import { AssistantMessage, PendingWrite } from '../../../shared/models/assistant.model';
import { ConversationListComponent } from '../conversation-list/conversation-list.component';
import { selectCurrentMatter, selectMatters } from '../../../store/crm/crm.selectors';
const SAMPLE_QUERIES = [
  'Show me the open tasks for this matter.',
  'What deadlines are coming up in the next 30 days?',
  'Draft a client intake summary for this matter.',
];

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-assistant-chat',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ConversationListComponent],
  template: `
    <section class="assistant-shell" [class.panel-mode]="panelMode" [class.expanded-mode]="expandedMode" aria-label="Assistant chat">
      <header class="assistant-header" *ngIf="!panelMode">
        <div class="header-title">
          <h2>Assistant</h2>
          <p>Ask questions about your active matter and documents.</p>
        </div>
        <div class="matter-selector" *ngIf="(matters$ | async) as matters">
          <label for="matter-select">Select Matter:</label>
          <select
            id="matter-select"
            [value]="(currentMatter$ | async)?._id || ''"
            (change)="onMatterChange($event)"
            class="matter-dropdown"
          >
            <option value="">-- Select a Matter --</option>
            <option *ngFor="let matter of matters" [value]="matter._id">
              {{ matter.title }}
            </option>
          </select>
        </div>
      </header>

      <nav class="panel-tabs" *ngIf="panelMode" role="tablist" aria-label="Assistant panels">
        <button class="panel-tab" role="tab"
          [class.active]="activeTab === 'history'"
          [attr.aria-selected]="activeTab === 'history'"
          (click)="setTab('history')">
          <i class="bi bi-clock-history"></i> History
        </button>
        <button class="panel-tab" role="tab"
          [class.active]="activeTab === 'chat'"
          [attr.aria-selected]="activeTab === 'chat'"
          (click)="setTab('chat')">
          <i class="bi bi-chat-text"></i> Chat
        </button>
      </nav>

      <!-- Matter selector row — shown in panel mode below the tabs -->
      <div class="panel-matter-row" *ngIf="panelMode && (matters$ | async) as matters">
        <i class="bi bi-briefcase panel-matter-icon"></i>
        <select
          id="panel-matter-select"
          [value]="(currentMatter$ | async)?._id || ''"
          (change)="onMatterChange($event)"
          class="panel-matter-select"
          aria-label="Select matter"
        >
          <option value="">Select a matter…</option>
          <option *ngFor="let matter of matters" [value]="matter._id">
            {{ matter.title }}
          </option>
        </select>
      </div>

      <p class="error-toast" role="alert" *ngIf="(error$ | async) as error">
        {{ error }}
      </p>

      <div class="assistant-content" *ngIf="(messages$ | async) as messages">
        <app-conversation-list
          class="sidebar-column"
          *ngIf="!panelMode || activeTab === 'history' || expandedMode"
          [conversations]="(conversations$ | async) || []"
          [currentConversationId]="(currentConversationId$ | async)"
          [loading]="(conversationsLoading$ | async) || false"
          [matterId]="(currentMatter$ | async)?._id"
          (selectConversation)="onSelectConversation($event)"
          (newConversation)="onNewConversation()"
        ></app-conversation-list>
        <ng-container *ngIf="(currentMatter$ | async) as activeMatter; else selectMatterState">
        <div class="conversation-column" *ngIf="!panelMode || activeTab === 'chat'">
          <section class="empty-state" *ngIf="messages.length === 0">
            <h3>Ask a question about your matter...</h3>
            <p>Try one of these sample prompts to get started:</p>

            <div class="sample-queries">
              <button
                type="button"
                class="sample-chip"
                *ngFor="let sample of sampleQueries"
                (click)="useSampleQuery(sample)"
              >
                {{ sample }}
              </button>
            </div>
          </section>

          <div class="message-list" aria-live="polite" #messageList>
            <article class="message" *ngFor="let message of messages" [class.user]="message.role === 'user'">
              <h3 *ngIf="!panelMode">{{ message.role === 'user' ? 'You' : 'Assistant' }}</h3>
              <p>{{ message.content }}</p>

              <div class="meta" *ngIf="message.role === 'assistant'">
                <span *ngIf="message.latencyMs">Latency: {{ message.latencyMs }}ms</span>
                <span *ngIf="message.traceId">Trace: {{ message.traceId }}</span>
                <span *ngIf="message.llmProvider">Provider: {{ message.llmProvider }}</span>
                <span *ngIf="message.llmModel">Model: {{ message.llmModel }}</span>
                <span *ngIf="message.llmFallbackUsed">Fallback: true</span>
                <span *ngIf="message.llmError">LLM Error: {{ message.llmError }}</span>
              </div>
            </article>

            <div class="message-skeleton" *ngIf="(loading$ | async) === true" aria-live="polite" aria-label="Loading response">
              <div class="skeleton-line w-80"></div>
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-72"></div>
            </div>
          </div>

          <div class="confirm-bar" *ngIf="pendingWrite$ | async as pw" role="alert">
            <div class="confirm-bar-body">
              <i class="bi bi-exclamation-triangle-fill confirm-bar-icon"></i>
              <span class="confirm-bar-text">{{ pw.description }}</span>
            </div>
            <div class="confirm-bar-actions">
              <button type="button" class="btn-confirm" (click)="onConfirmWrite(pw)" [disabled]="(loading$ | async) === true">
                Confirm
              </button>
              <button type="button" class="btn-cancel" (click)="onCancelWrite(pw)" [disabled]="(loading$ | async) === true">
                Cancel
              </button>
            </div>
          </div>

          <form class="composer" [formGroup]="form" (ngSubmit)="send($event)">
            <label for="assistant-query" class="sr-only">Ask the assistant</label>
            <div class="composer-row" [class.focused]="composerFocused">
              <textarea
                #queryInput
                id="assistant-query"
                formControlName="query"
                rows="1"
                placeholder="Ask anything…"
                (keydown)="onComposerKeydown($event)"
                (focus)="composerFocused = true"
                (blur)="composerFocused = false"
                (input)="autoGrow($event)"
              ></textarea>
              <select
                id="ai-provider"
                formControlName="provider"
                class="model-chip"
                aria-label="AI host"
                title="Select AI host"
              >
                <option value="groq">Groq</option>
                <option value="bedrock">Bedrock</option>
                <option value="ollama">Ollama (local)</option>
              </select>
              <select
                *ngIf="form.controls.provider.value === 'groq'"
                id="ai-model"
                formControlName="model"
                class="model-chip model-chip--wide"
                aria-label="Groq model"
                title="Select Groq model"
              >
                <option value="openai/gpt-oss-120b">GPT-OSS 120B</option>
                <option value="openai/gpt-oss-20b">GPT-OSS 20B</option>
                <option value="qwen/qwen3.6-27b">Qwen 3.6 27B</option>
              </select>
              <select
                *ngIf="form.controls.provider.value === 'ollama'"
                id="ai-model-ollama"
                formControlName="model"
                class="model-chip model-chip--wide"
                aria-label="Ollama model"
                title="Select local Ollama model — requires 'ollama serve' running on your machine"
              >
                <option value="qwen2.5:14b-instruct">Qwen2.5 14B</option>
                <option value="llama3.1:8b">Llama 3.1 8B</option>
                <option value="qwen2.5-coder:14b-instruct">Qwen2.5 Coder 14B</option>
                <option value="hhao/qwen2.5-coder-tools:14b">Qwen2.5 Coder Tools 14B</option>
              </select>
              <button
                type="button"
                class="send-btn"
                (click)="send($event)"
                [disabled]="(loading$ | async) === true"
                aria-label="Send query"
              >
                <ng-container *ngIf="(loading$ | async); else sendIcon">
                  <i class="bi bi-hourglass-split"></i>
                </ng-container>
                <ng-template #sendIcon>
                  <i class="bi bi-send-fill"></i>
                </ng-template>
              </button>
            </div>
            <small class="composer-hint" *ngIf="composerFocused">
              Enter to send · Shift+Enter for new line
            </small>
          </form>
        </div>

        </ng-container>

        <ng-template #selectMatterState>
          <section class="no-matter-panel" *ngIf="!panelMode || activeTab === 'chat'">
            <h3>Select a matter to continue</h3>
            <p>Active chats are automatically docked when no matter is selected.</p>
            <p class="no-matter-cta">
              <i class="bi bi-arrow-up"></i> Select a matter above to begin
            </p>
          </section>
        </ng-template>
      </div>
    </section>
  `,
  styles: [
    `
      .assistant-shell {
        display: grid;
        gap: 0.9rem;
        max-width: 1180px;
        margin: 0 auto;
      }

      .assistant-content {
        display: grid;
        gap: 0.9rem;
        grid-template-columns: 130px minmax(0, 1fr) 220px;
        align-items: start;
        padding: 0 1.25rem;
      }

      .sidebar-column {
        position: sticky;
        top: 1rem;
        border-right: 1px solid var(--g8r-border);
        padding-right: 0.75rem;
        min-width: 0;
        overflow: hidden;
      }

      .citations-column {
        border-left: 1px solid var(--g8r-border);
        padding-left: 0.75rem;
        min-width: 0;
        overflow: hidden;
      }

      .conversation-column {
        display: grid;
        gap: 0.8rem;
      }

      .no-matter-panel {
        display: grid;
        gap: 0.45rem;
        border: 1px dashed var(--g8r-navy-200);
        border-radius: 1rem;
        padding: 1rem;
        background: linear-gradient(180deg, rgba(237,244,251,0.8), rgba(255,255,255,0.98));
      }

      .no-matter-panel h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--g8r-navy-800);
      }

      .no-matter-panel p {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--g8r-grey-text);
      }

      .no-matter-cta {
        margin: 0.5rem 0 0;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--g8r-gold-700);
      }

      .assistant-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.5rem;
        padding: 1.1rem 1.25rem;
        border: 1px solid var(--g8r-border);
        border-radius: 1rem;
        background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247,249,252,0.98));
        box-shadow: var(--g8r-shadow-sm);
      }

      .header-title h2 {
        margin: 0;
        color: var(--g8r-navy-900);
      }

      .assistant-header p {
        color: var(--g8r-grey-text);
        margin: 0.25rem 0 0 0;
      }

      .matter-selector {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        min-width: 280px;
      }

      .matter-selector label {
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--g8r-navy-800);
      }

      .matter-dropdown {
        padding: 0.5rem;
        border: 1px solid #a8bccf;
        border-radius: var(--g8r-radius);
        font-size: 0.9rem;
        background: white;
        cursor: pointer;
      }

      .matter-dropdown:focus {
        outline: none;
        box-shadow: var(--g8r-shadow-focus);
        border-color: var(--g8r-navy-500);
      }

      .empty-state {
        display: grid;
        gap: 0.4rem;
        padding: 0.75rem;
        background: transparent;
      }

      .empty-state h3 {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--g8r-navy-800);
      }

      .empty-state p {
        margin: 0;
        font-size: 0.75rem;
        color: var(--g8r-grey-text);
      }

      .sample-queries {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        margin-top: 0.15rem;
      }

      .sample-chip {
        background: var(--g8r-navy-50);
        color: var(--g8r-navy-700);
        border: 1px solid var(--g8r-navy-100);
        font-weight: 500;
        font-size: 0.78rem;
        padding: 0.35rem 0.6rem;
        border-radius: 0.45rem;
        text-align: left;
        cursor: pointer;
      }

      .sample-chip:hover {
        background: var(--g8r-gold-50, #fffbeb);
        color: var(--g8r-gold-900);
        border-color: var(--g8r-gold-200);
      }

      .error-toast {
        margin: 0;
        border: 1px solid rgba(192, 57, 43, 0.28);
        background: #fff5f2;
        color: var(--g8r-danger);
        border-radius: 0.9rem;
        padding: 0.65rem 0.8rem;
      }

      .message-list {
        display: grid;
        gap: 0.75rem;
        max-height: 58vh;
        overflow: auto;
        border: none;
        border-radius: 0;
        padding: 0.75rem;
        background: transparent;
        box-shadow: none;
      }

      .message {
        display: grid;
        gap: 0.35rem;
        padding: 0.6rem 0.75rem;
        border: 1px solid var(--g8r-border);
        border-radius: 0.85rem;
        min-width: 0;
        font-size: 0.8125rem;
        line-height: 1.55;
      }

      .message.user {
        background: var(--g8r-navy-50);
        border-color: var(--g8r-navy-200);
        margin-left: 2.5rem;
        border-bottom-right-radius: 3px;
      }

      .message:not(.user) {
        background: transparent;
        border-color: transparent;
        margin-right: 0;
        border-bottom-left-radius: 3px;
        padding-left: 0;
        padding-right: 0;
      }

      .message h3 {
        margin: 0;
        font-size: 0.95rem;
        color: var(--g8r-navy-900);
      }

      .message p {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        color: var(--g8r-grey-text);
        font-size: 0.72rem;
      }

      .message-skeleton {
        display: grid;
        gap: 0.5rem;
        padding: 0.75rem;
        border: 1px solid var(--g8r-border);
        border-radius: 0.85rem;
        margin-right: 1.5rem;
      }

      .skeleton-line {
        height: 12px;
        border-radius: 6px;
        background: linear-gradient(90deg, var(--g8r-navy-100) 25%, var(--g8r-navy-50) 50%, var(--g8r-navy-100) 75%);
        background-size: 200% 100%;
        animation: shimmer 1.4s infinite;
      }

      .skeleton-line.w-80 { width: 80%; }
      .skeleton-line.w-60 { width: 60%; }
      .skeleton-line.w-72 { width: 72%; }

      @keyframes shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      .composer {
        flex-shrink: 0;
        padding: 0.6rem 0.75rem 0.5rem;
        border-top: 1px solid var(--g8r-border);
        background: var(--g8r-white);
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
      }

      .composer-row {
        display: flex;
        align-items: flex-end;
        gap: 0.4rem;
        border: 1px solid #a8bccf;
        border-radius: 0.85rem;
        padding: 0.45rem 0.5rem 0.45rem 0.75rem;
        background: #fff;
        transition: box-shadow 0.15s, border-color 0.15s;
      }

      .composer-row.focused {
        box-shadow: var(--g8r-shadow-focus);
        border-color: var(--g8r-navy-500);
      }

      .composer-row textarea {
        flex: 1;
        min-width: 0;
        resize: none;
        min-height: 24px;
        max-height: 96px;
        padding: 0;
        border: none;
        outline: none;
        font: inherit;
        font-size: 0.875rem;
        color: var(--g8r-navy-900);
        background: transparent;
        line-height: 1.5;
        overflow-y: auto;
      }

      .model-chip {
        flex-shrink: 0;
        border: 1px solid var(--g8r-border);
        border-radius: var(--g8r-radius-sm);
        font-size: 0.72rem;
        font-weight: 600;
        font-family: var(--g8r-font-mono);
        color: var(--g8r-navy-600);
        background: var(--g8r-navy-50);
        padding: 0.2rem 0.35rem;
        cursor: pointer;
        max-width: 80px;
        appearance: auto;
      }

      .model-chip:focus {
        outline: none;
        border-color: var(--g8r-gold-500);
      }

      .model-chip--wide {
        max-width: 128px;
      }

      .send-btn {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        background: var(--g8r-gold-500);
        border: none;
        border-radius: 8px;
        color: var(--g8r-navy-950);
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.15s;
      }

      .send-btn:hover:not(:disabled) {
        background: var(--g8r-gold-600);
      }

      .send-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .composer-hint {
        color: var(--g8r-grey-text);
        font-size: 0.72rem;
        padding-left: 0.1rem;
        min-height: 1em;
      }

      .confirm-bar {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        background: #fff8e6;
        border: 1px solid #f0c040;
        border-radius: 0.85rem;
        box-shadow: var(--g8r-shadow-sm);
      }

      .confirm-bar-body {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        flex: 1;
        min-width: 0;
      }

      .confirm-bar-icon {
        color: #c07000;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .confirm-bar-text {
        font-size: 0.9rem;
        color: var(--g8r-navy-900);
        white-space: pre-wrap;
        overflow-wrap: break-word;
      }

      .confirm-bar-actions {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
      }

      .btn-confirm {
        background: var(--g8r-gold-500);
        border: 1px solid var(--g8r-gold-500);
        color: var(--g8r-navy-950);
        border-radius: 9999px;
        padding: 0.4rem 0.85rem;
        font-weight: 600;
        font-size: 0.85rem;
      }

      .btn-confirm:hover:not(:disabled) {
        background: var(--g8r-gold-600);
        border-color: var(--g8r-gold-600);
      }

      .btn-cancel {
        background: transparent;
        border: 1px solid var(--g8r-border);
        color: var(--g8r-grey-text);
        border-radius: 9999px;
        padding: 0.4rem 0.85rem;
        font-size: 0.85rem;
      }

      .btn-cancel:hover:not(:disabled) {
        background: var(--g8r-navy-50);
        color: var(--g8r-navy-700);
      }

      .btn-confirm:disabled,
      .btn-cancel:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      }

      @media (max-width: 767px) {
        .assistant-content {
          grid-template-columns: 1fr;
        }

        .sidebar-column {
          display: none;
        }

        .message-list {
          max-height: 52vh;
        }

        .assistant-header {
          flex-direction: column;
          align-items: flex-start;
        }

        .matter-selector {
          width: 100%;
        }
      }

      @media (max-width: 1023px) {
        .assistant-content {
          grid-template-columns: 240px 1fr 300px;
        }
      }

      /* Panel mode matter selector row */
      .panel-matter-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.45rem 0.75rem;
        background: var(--g8r-navy-50);
        border-bottom: 1px solid var(--g8r-border);
        flex-shrink: 0;
      }

      .panel-matter-icon {
        color: var(--g8r-navy-500);
        font-size: 0.85rem;
        flex-shrink: 0;
      }

      .panel-matter-select {
        flex: 1;
        min-width: 0;
        padding: 0.3rem 0.5rem;
        border: 1px solid var(--g8r-border);
        border-radius: var(--g8r-radius-sm);
        font-size: 0.82rem;
        font-family: inherit;
        background: #fff;
        color: var(--g8r-navy-900);
        cursor: pointer;
      }

      .panel-matter-select:focus {
        outline: none;
        box-shadow: var(--g8r-shadow-focus);
        border-color: var(--g8r-navy-500);
      }

      /* Panel mode — single column tabbed layout */
      .assistant-shell.panel-mode {
        max-width: none;
        margin: 0;
        width: 100%;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .panel-tabs {
        display: flex;
        border-bottom: 1px solid var(--g8r-border);
        background: var(--g8r-white);
        flex-shrink: 0;
      }

      .panel-tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
        padding: 0.6rem 0.5rem;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        color: var(--g8r-grey-text);
        font-size: 0.82rem;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.15s, border-color 0.15s;
      }

      .panel-tab:hover {
        color: var(--g8r-navy-700);
        background: var(--g8r-navy-50);
      }

      .panel-tab.active {
        color: var(--g8r-navy-700);
        border-bottom-color: var(--g8r-gold-500);
        font-weight: 600;
        background: transparent;
      }

      .assistant-shell.panel-mode .assistant-content {
        grid-template-columns: 1fr;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: 0;
      }

      .assistant-shell.panel-mode .sidebar-column {
        position: static;
        flex: 1;
        overflow-x: hidden;
        overflow-y: auto;
        min-height: 0;
      }

      .assistant-shell.panel-mode:not(.expanded-mode) .sidebar-column {
        border-right: none;
        padding-right: 0;
      }

      .assistant-shell.panel-mode .conversation-column {
        flex: 1;
        overflow: hidden;
        min-height: 0;
        display: flex;
        flex-direction: column;
        position: relative;
        padding-bottom: 76px;
      }

      .assistant-shell.panel-mode .message-list {
        max-height: none;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        border: none;
        background: transparent;
        box-shadow: none;
        padding: 0.75rem;
        border-radius: 0;
      }

      .assistant-shell.panel-mode .composer {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
      }

      .assistant-shell.panel-mode .citations-column {
        flex: 1;
        overflow: auto;
        min-height: 0;
      }

      .assistant-shell.panel-mode:not(.expanded-mode) .citations-column {
        border-left: none;
        padding-left: 0;
      }

      /* Three-column layout when expanded to full workspace */
      .assistant-shell.panel-mode.expanded-mode .assistant-content {
        grid-template-columns: 130px minmax(0, 1fr) 220px;
        display: grid;
        flex: 1;
        overflow: hidden;
        padding: 0 1.25rem;
      }

      .assistant-shell.panel-mode.expanded-mode .sidebar-column,
      .assistant-shell.panel-mode.expanded-mode .citations-column {
        display: flex;
        flex-direction: column;
        flex: unset;
        overflow: auto;
        min-height: 0;
      }

      /* Hide tab bar — all panels visible simultaneously in expanded mode */
      .assistant-shell.panel-mode.expanded-mode .panel-tabs {
        display: none;
      }

      .assistant-shell.panel-mode.expanded-mode .panel-matter-row {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
      }
    `,
  ],
})
export class AssistantChatComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() panelMode = false;
  @Input() expandedMode = false;

  activeTab: 'history' | 'chat' = 'chat';
  composerFocused = false;

  private destroy$ = new Subject<void>();
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;
  private previousMatterId: string | null = null;

  @ViewChild('messageList')
  private messageListRef?: ElementRef<HTMLDivElement>;

  sampleQueries = SAMPLE_QUERIES;

  messages$ = this.store.select(selectAssistantMessages);
  loading$ = this.store.select(selectAssistantLoading);
  error$ = this.store.select(selectAssistantError);
  currentMatter$ = this.store.select(selectCurrentMatter);
  currentConversationId$ = this.store.select(selectCurrentConversationId);
  conversations$ = this.store.select(selectConversations);
  conversationsLoading$ = this.store.select(selectConversationsLoading);
  matters$ = this.store.select(selectMatters);
  pendingWrite$ = this.store.select(selectPendingWrite);
  adminSessionId$ = this.store.select(selectAdminSessionId);

  form = this.formBuilder.group({
    query: ['', [Validators.required, Validators.minLength(3)]],
    provider: ['groq'],
    model: ['openai/gpt-oss-120b'],
  });

  // Sensible default model per provider — keeps the model control from silently
  // holding a stale value (e.g. a Groq model name) after switching provider.
  private static readonly DEFAULT_MODEL_BY_PROVIDER: Record<string, string> = {
    groq: 'openai/gpt-oss-120b',
    ollama: 'qwen2.5:14b-instruct',
  };

  constructor(private store: Store, private formBuilder: FormBuilder) {
    this.form.controls.provider.valueChanges.subscribe((provider) => {
      const defaultModel = AssistantChatComponent.DEFAULT_MODEL_BY_PROVIDER[provider ?? ''];
      if (defaultModel) {
        this.form.controls.model.setValue(defaultModel);
      }
    });
  }

  ngOnInit(): void {
    // Load matters list for the selector
    this.store.dispatch(CrmActions.loadMatters({}));

    // Keep assistant chat scoped to currently selected matter.
    this.currentMatter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((matter) => {
        const currentMatterId = matter?._id || null;

        if (!currentMatterId) {
          this.previousMatterId = null;
          this.store.dispatch(AssistantActions.clearMessages());
          return;
        }

        if (currentMatterId !== this.previousMatterId) {
          this.previousMatterId = currentMatterId;
          this.store.dispatch(AssistantActions.clearMessages());
        }

        this.store.dispatch(
          AssistantActions.loadConversations({
            matterId: currentMatterId,
            includeArchived: false,
          })
        );
      });

    // Keep newest responses visible without requiring manual scrolling.
    this.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scheduleScrollToBottom());

    this.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        if (loading) {
          this.scheduleScrollToBottom();
        }
      });
  }

  ngAfterViewInit(): void {
    this.scheduleScrollToBottom();
  }

  @ViewChild('queryInput') queryInput?: ElementRef<HTMLTextAreaElement>;

  send(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    
    const nativeValue = this.queryInput?.nativeElement?.value?.trim();
    const formValue = this.form.controls.query.value?.trim();
    const value = nativeValue || formValue || '';

    console.log('[AssistantChat] send() called. Value:', value);

    if (value.length < 3) {
      console.warn('[AssistantChat] Query too short! Must be at least 3 characters.');
      alert('Query is too short! Must be at least 3 characters.');
      this.form.controls.query.markAsTouched();
      return;
    }

    this.store.dispatch(AssistantActions.clearError());
    console.log('[AssistantChat] Dispatching sendQuery action...');

    this.store.select(selectCurrentConversationId)
      .pipe(take(1))
      .subscribe({
        next: (conversationId) => {
          console.log('[AssistantChat] Conversation ID:', conversationId);
          const selectedProvider = this.form.controls.provider.value || 'groq';
          // Model choice applies to Groq and Ollama — Bedrock's model is fixed server-side.
          const selectedModel = (selectedProvider === 'groq' || selectedProvider === 'ollama')
            ? (this.form.controls.model.value || undefined)
            : undefined;
          this.store.dispatch(
            AssistantActions.sendQuery({
              query: value,
              provider: selectedProvider,
              model: selectedModel,
              conversationId: conversationId ?? undefined,
              autoCreateConversation: !conversationId,
            })
          );

          // Only reset the query field — resetting the whole form group can
          // desync the SelectControlValueAccessor under OnPush, causing the
          // provider control to revert to its initial value ('groq') on the
          // next send even though the user selected 'bedrock'.
          this.form.controls.query.reset('');
          if (this.queryInput?.nativeElement) {
            this.queryInput.nativeElement.value = '';
          }
        },
        error: (err) => {
          console.error('[AssistantChat] Error getting conversation ID:', err);
          alert('Failed to get conversation ID. See console for details.');
        }
      });
  }

  onConfirmWrite(pendingWrite: PendingWrite): void {
    this.adminSessionId$.pipe(take(1)).subscribe((sessionId) => {
      this.store.dispatch(
        AssistantActions.confirmPendingWrite({
          pendingWrite,
          sessionId: sessionId ?? `session-${Date.now()}`,
        })
      );
    });
  }

  onCancelWrite(pendingWrite: PendingWrite): void {
    this.adminSessionId$.pipe(take(1)).subscribe((sessionId) => {
      this.store.dispatch(
        AssistantActions.cancelPendingWrite({
          pendingWrite,
          sessionId: sessionId ?? `session-${Date.now()}`,
        })
      );
    });
  }

  useSampleQuery(query: string): void {
    this.form.patchValue({ query });
    this.send();
  }

  autoGrow(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      (event.target as HTMLTextAreaElement | null)?.blur();
      this.store.dispatch(AssistantActions.clearError());
    }
  }

  onSelectConversation(conversationId: string): void {
    this.store.dispatch(AssistantActions.selectConversation({ conversationId }));
  }

  onMatterChange(event: Event): void {
    const matterId = (event.target as HTMLSelectElement).value;
    if (matterId) {
      // Load matter detail which will update currentMatter in the store
      this.store.dispatch(CrmActions.loadMatterDetail({ matterId }));
      this.store.dispatch(AssistantActions.clearMessages());
      return;
    }

    this.store.dispatch(CrmActions.clearCurrentMatter());
    this.store.dispatch(AssistantActions.clearMessages());
  }

  onNewConversation(): void {
    // Clear existing messages and history before starting a new conversation.
    this.store.dispatch(AssistantActions.clearMessages());
    this.currentMatter$
      .pipe(take(1))
      .subscribe((matter) => {
        if (matter && matter._id) {
          this.store.dispatch(
            AssistantActions.newConversation({
              title: 'New Conversation',
              matterId: matter._id,
            })
          );
        }
      });
  }

  setTab(tab: 'history' | 'chat'): void {
    this.activeTab = tab;
  }

  private scheduleScrollToBottom(): void {
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    // Wait for DOM updates so new content contributes to scroll height.
    this.scrollTimer = setTimeout(() => {
      const el = this.messageListRef?.nativeElement;
      if (!el) {
        this.scrollTimer = null;
        return;
      }

      el.scrollTop = el.scrollHeight;
      this.scrollTimer = null;
    }, 0);
  }

  ngOnDestroy(): void {
    // Auto-dock active chat when leaving assistant module.
    this.store.dispatch(AssistantActions.clearMessages());
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
